import React, { useState, useCallback, useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import { useIxAuth } from 'contexts/auth-context'

import { setTargets, setVisible } from 'slices/spotlight'

import Popup from 'devextreme-react/popup'
import MultiView, { Item } from 'devextreme-react/multi-view'
import Button from 'devextreme-react/button'

import client from 'api'

import V5Logo from 'assets/images/V-pink.svg?react'
import './welcome-modal.scss'


export const IxWelcomeModal = () => {
    const { user } = useIxAuth()

    const [welcomeVisible, setWelcomeVisible] = useState(false)
    const [slideshowVisible, setSlideshowVisible] = useState(false)
    const [tabIndex, setTabIndex] = useState(0)
    const [spotlightTargets, setSpotlightTargets] = useState([])

    const dispatch = useDispatch()

    const { t, i18n } = useTranslation()

    const [images, setImages] = useState<any[]>([])

    useEffect(() => {
        if (!user) { return }

        const promises = [
            client.get('/api/ixweb/get_file/?subpath=data/slideshow&filename=texts.json'),
            client.get('/api/ixweb/list_folder/?subpath=data/slideshow&foldername=slideshow')
        ]

        Promise.allSettled(promises).then(([textsRes, filesRes]: any[]) => {
            const texts = textsRes.status === 'fulfilled' ? textsRes.value.data : {}
            if (filesRes.status === 'fulfilled') {
                const imgPromises = filesRes.value.data
                    .filter((img: string) => img.endsWith('.jpg') || img.endsWith('.jpeg') ||img.endsWith('.png'))
                    .map((img: string) => client.get(`/api/ixweb/get_file/?subpath=data/slideshow&filename=${img}`, { responseType: 'blob' }))
                Promise.all(imgPromises).then(res => {
                    const imgConversionPromises = res.map((r: any) => {
                        return new Promise((resolve) => {
                            const filename = r.config.url.match(/[?&]filename=(.*)/)[1]
                            const fr = new FileReader();
                            fr.onload = __ => resolve({ img: fr.result, title: texts[filename]?.title, desc: texts[filename]?.desc })
                            fr.readAsDataURL(r.data)
                        })
                    })
                    Promise.all(imgConversionPromises).then(imgs => {
                        setImages(imgs)
                    })
                })
            }
        })

        client.get('/api/ixweb/get_file/?subpath=data/slideshow&filename=spotlight.json').then(res => {
            if (res.status === 200) {
                setSpotlightTargets(res.data)
            }
        })
    }, [user])

    const onNextButtonClick = useCallback(() => {
        if (tabIndex + 1 < images.length) {
            setTabIndex(i => i + 1)
        } else {
            setSlideshowVisible(false)
            setTabIndex(0)
            spotlightTargets && dispatch(setTargets(spotlightTargets))
            dispatch(setVisible(true))
        }
    }, [tabIndex, images])

    const onNextClick = useCallback(() => { setTabIndex(i => i + 1 < images.length ? i + 1 : 0) }, [images])
    const onPrevClick = useCallback(() => { setTabIndex(i => i - 1 >= 0 ? i - 1 : images.length - 1) }, [images])
    const onCloseClick = useCallback(() => {
        setSlideshowVisible(false)
        setTabIndex(0)
    }, [])

    return (
        <div>
            <Button icon='help' disabled={images.length === 0} onClick={ () => setWelcomeVisible(true) } />
            <Popup
                visible={ welcomeVisible }
                onHiding={ () => setWelcomeVisible(false) }
                showTitle={ false }
                width='568px'
                height='auto'
                wrapperAttr={ { class: 'ix-welcome-modal' } }
            >
                <div>
                    <div className='ix-title'>
                        <div className='ix-logo'>
                            <V5Logo width={32} height={32} />
                        </div>
                        <div className='ix-welcome-text'>
                            <h3>{ t('welcomeslideshow:welcome-title') }</h3>
                            <p>{ t('welcomeslideshow:welcome-desc') }</p>
                        </div>
                    </div>

                    <div className='ix-welcome-cards'>
                        <div className='ix-welcome-card'>
                            <div>
                                <div>
                                    <i className='dx-icon dx-icon-preferences'/>
                                    <h5>{ t('welcomeslideshow:card1.title') }</h5>
                                </div>
                                <span>{ t('welcomeslideshow:card1.desc') }</span>
                            </div>
                        </div>
                        <div className='ix-welcome-card'>
                            <div>
                                <div>
                                    <i className='dx-icon dx-icon-preferences'/>
                                    <h5>{ t('welcomeslideshow:card2.title') }</h5>
                                </div>
                                <span>{ t('welcomeslideshow:card2.desc') }</span>
                            </div>
                        </div>
                        <div className='ix-welcome-card'>
                            <div>
                                <div>
                                    <i className='dx-icon dx-icon-preferences'/>
                                    <h5>{ t('welcomeslideshow:card3.title') }</h5>
                                </div>
                                <span>{ t('welcomeslideshow:card3.desc') }</span>
                            </div>
                        </div>
                        <div className='ix-welcome-card'>
                            <div>
                                <div>
                                    <i className='dx-icon dx-icon-preferences'/>
                                    <h5>{ t('welcomeslideshow:card4.title') }</h5>
                                </div>
                                <span>{ t('welcomeslideshow:card4.desc') }</span>
                            </div>
                        </div>
                    </div>

                    <div className='ix-welcome-buttons'>
                        <Button onClick={ () => setWelcomeVisible(false) }>{ t('welcomeslideshow:button.later') }</Button>
                        <Button onClick={ () => { setWelcomeVisible(false); setSlideshowVisible(true) } }>{ t('welcomeslideshow:button.start-intro') }</Button>
                    </div>
                </div>
            </Popup>
            <Popup
                visible={ slideshowVisible }
                onHiding={ () => setSlideshowVisible(false) }
                showTitle={ false }
                width='768px'
                height='auto'
                wrapperAttr={ { class: 'ix-slideshow-modal' } }
            >
                <div>
                    <div className='ix-slideshow'>
                        <MultiView
                            selectedIndex={ tabIndex }
                            deferRendering={ false }
                        >
                            { images.map((item: any, i: number) => (
                                <Item key={i}>
                                        <img
                                            style={ { borderRadius: '2px' } }
                                            src={ item.img }
                                            width={1536}
                                            height={1024}
                                        />
                                </Item>)
                            ) }
                        </MultiView>
                        <Button
                            style={ { position: 'absolute', top: '50%', left: '10px' } }
                            icon='chevronleft'
                            disabled={ tabIndex === 0 }
                            onClick={ onPrevClick }
                        />
                        <Button
                            style={ { position: 'absolute', top: '50%', right: '10px' } }
                            icon='chevronright'
                            disabled={ tabIndex === images.length - 1 }
                            onClick={ onNextClick }
                        />
                        <Button
                            style={ { position: 'absolute', top: '10px', right: '10px', backgroundColor: '#00000011' } }
                            icon='close'
                            onClick={ onCloseClick }
                        />
                    </div>

                    {
                        images[tabIndex] && images[tabIndex].title ? (
                            <div className='ix-slideshow-text' style={ { padding: '0 16px 16px 16px' } }>
                                <h3>
                                    { images[tabIndex].title[i18n.language] }
                                </h3>
                                <small>
                                    { images[tabIndex].desc[i18n.language] }
                                </small>
                            </div>
                        ) : null
                    }

                    <div className='ix-tabs-indicator'>
                        <div style={ { height: '10px' } }>
                            { images.map((_, i) => (
                                <span
                                key={i}
                                className={`item${i === tabIndex ? ' current-item' : ''}`}
                                onClick={ () => setTabIndex(i) }
                                />
                            )) }
                        </div>
                        <div className='ix-tabs-indicator-button'>
                            { tabIndex + 1 }/{ images.length }
                            <Button type='success' onClick={ onNextButtonClick }>{ t(`welcomeslideshow:button.${tabIndex < images.length - 1 ? 'next' : 'finish'}`) }</Button>
                        </div>
                    </div>
                </div>
            </Popup>
        </div>
    )
}
